import { isNil } from 'lodash-es';

import { formatPrice } from 'utils';
import { Transfer2 } from 'services/BackendApi/types';

import { ETransferDirection } from './subdomains/transfers/types';
import {
  buildDateTimePrefix,
  buildGuestsCountLine,
  buildOptionsLine,
  formatAncillaryTitle,
  getAncillaryRequestIndex,
  IAncillaryOption,
  isAncillaryProduct,
  toAncillaryOptions,
} from './addedAncillaryShared';

export interface IAddedAncillaryTransferLeg {
  date: string;
  time?: string;
  fromName?: string;
  toName?: string;
}

export interface IAddedAncillaryTransfer {
  key: string;
  name: string;
  baseProductName?: string;
  variantName?: string;
  units: number;
  total: string | null;
  totalCents: number | null;
  isOnRequest: boolean;
  isReturn: boolean;
  isCustom: boolean;
  numberOfAdults: number;
  numberOfChildren: number;
  legs: IAddedAncillaryTransferLeg[];
  options: IAncillaryOption[];
  ancillaryItemIndex: number;
}

const LEG_DIRECTION_ORDER = [ETransferDirection.IN, ETransferDirection.OUT];

const TRANSFER_CLIPBOARD_SEPARATOR = ' - ';

export const getLegDate = (leg: Transfer2): string =>
  leg.ancillaryData?.userSelections?.selectedDate ?? leg.customData?.selectedDate ?? leg.dates?.[0] ?? '';

export const getLegTime = (leg: Transfer2): string | undefined =>
  leg.ancillaryData?.userSelections?.time ?? leg.customData?.time;

const toDefinedName = (name?: string): string | undefined => (name ? name : undefined);

const getLegPlaceNames = (leg: Transfer2): { fromName?: string; toName?: string } => {
  if (leg.customData) {
    return {
      fromName: toDefinedName(leg.customData.transferLocations?.from),
      toName: toDefinedName(leg.customData.transferLocations?.to),
    };
  }

  const transferLocations = leg.ancillaryData?.userSelections?.transferLocations;

  return {
    fromName: toDefinedName(transferLocations?.from?.placeName),
    toName: toDefinedName(transferLocations?.to?.placeName),
  };
};

const getLegDirectionRank = (leg: Transfer2): number => {
  const index = LEG_DIRECTION_ORDER.findIndex(direction => direction === leg.meta?.direction);

  return index === -1 ? LEG_DIRECTION_ORDER.length : index;
};

export const sortLegs = (legs: Transfer2[]): Transfer2[] =>
  [...legs].sort((legA, legB) => {
    const directionDifference = getLegDirectionRank(legA) - getLegDirectionRank(legB);

    if (directionDifference !== 0) {
      return directionDifference;
    }

    return getLegDate(legA).localeCompare(getLegDate(legB));
  });

export const toTransferLegs = (legs: Transfer2[]): IAddedAncillaryTransferLeg[] =>
  sortLegs(legs).map((leg, legIndex) => {
    const { fromName, toName } = getLegPlaceNames(leg);
    const isCatalogueReturnLeg = legIndex > 0 && isNil(leg.customData);

    return {
      date: getLegDate(leg),
      time: getLegTime(leg),
      fromName: isCatalogueReturnLeg ? toName : fromName,
      toName: isCatalogueReturnLeg ? fromName : toName,
    };
  });

export const getAncillaryReturnHash = (transfer: Transfer2): string | undefined =>
  transfer.ancillaryData?.userSelections?.returnHash ?? transfer.customData?.returnHash;

export interface IAddedAncillaryTransferGroup {
  requestIndex: number;
  legs: Transfer2[];
}

export const buildAddedAncillaryTransfer = ({
  requestIndex,
  legs,
}: IAddedAncillaryTransferGroup): IAddedAncillaryTransfer => {
  const [transfer] = legs;
  const totalCents = legs.reduce((accumulator, leg) => accumulator + (leg.totalCents ?? 0), 0);
  const isOnRequest = legs.some(leg => leg.isOnRequest || isNil(leg.totalCents));
  const guestSelections = transfer.ancillaryData?.userSelections ?? transfer.customData;
  const ancillaryProduct = transfer.ancillaryData?.product;

  return {
    key: `added-ancillary-transfer-${requestIndex}`,
    name: transfer.customData ? transfer.title : transfer.product?.name ?? transfer.title,
    baseProductName: ancillaryProduct?.baseProductName,
    variantName: ancillaryProduct?.variantName ?? undefined,
    units: transfer.ancillaryData?.userSelections?.perUnitQuantityOverride ?? 1,
    total: isOnRequest ? null : formatPrice(totalCents / 100),
    totalCents: isOnRequest ? null : totalCents,
    isOnRequest,
    isReturn: getAncillaryReturnHash(transfer) !== undefined,
    isCustom: transfer.customData != null,
    numberOfAdults: guestSelections?.numberOfAdults ?? 0,
    numberOfChildren: guestSelections?.agesOfAllChildren?.length ?? 0,
    legs: toTransferLegs(legs),
    options: toAncillaryOptions(transfer),
    ancillaryItemIndex: requestIndex,
  };
};

export const isAncillaryTransferLeg = (transfer: Transfer2): boolean =>
  isAncillaryProduct(transfer.product) || transfer.customData != null;

export const filterAncillaryTransferLegs = (transfers: Transfer2[]): Transfer2[] =>
  transfers.filter(isAncillaryTransferLeg);

export const groupAncillaryTransferLegs = (legs: Transfer2[]): IAddedAncillaryTransferGroup[] =>
  legs.reduce<IAddedAncillaryTransferGroup[]>((groups, leg) => {
    const requestIndex = getAncillaryRequestIndex(leg);
    const existingGroup = groups.find(group => group.requestIndex === requestIndex);

    if (existingGroup) {
      existingGroup.legs.push(leg);
      return groups;
    }

    return [...groups, { requestIndex, legs: [leg] }];
  }, []);

export const buildAddedAncillaryTransfers = (transfers: Transfer2[]): IAddedAncillaryTransfer[] =>
  groupAncillaryTransferLegs(filterAncillaryTransferLegs(transfers)).map(buildAddedAncillaryTransfer);

export const buildGuestsLine = (transfer: IAddedAncillaryTransfer): string => {
  const tripType = transfer.isReturn ? 'Return' : 'One Way';

  return `${tripType} Transfer - ${buildGuestsCountLine(transfer.numberOfAdults, transfer.numberOfChildren)}`;
};

export const formatAncillaryTransferTitle = (transfer: IAddedAncillaryTransfer): string =>
  formatAncillaryTitle(transfer);

export const buildLegRouteLine = (leg: IAddedAncillaryTransferLeg): string =>
  [leg.fromName, leg.toName].filter(Boolean).join(' To ');

export const buildLegLine = (leg: IAddedAncillaryTransferLeg): string =>
  [buildDateTimePrefix(leg), buildLegRouteLine(leg)].filter(Boolean).join(' ');

const buildLegAndOptionLines = (transfer: IAddedAncillaryTransfer): string[] => [
  ...transfer.legs.map(buildLegLine),
  buildOptionsLine(transfer.options),
];

export const buildAncillaryTransferDetailLines = (transfer: IAddedAncillaryTransfer): string[] =>
  [`${buildGuestsLine(transfer)}.`, ...buildLegAndOptionLines(transfer)].filter(Boolean);

export const buildAncillaryTransferClipboardLine = (transfer: IAddedAncillaryTransfer): string =>
  [
    `${formatAncillaryTitle(transfer, TRANSFER_CLIPBOARD_SEPARATOR)}, ${buildGuestsLine(transfer)}`,
    ...buildLegAndOptionLines(transfer),
  ]
    .filter(Boolean)
    .join(TRANSFER_CLIPBOARD_SEPARATOR);
